import {
  ApolloClient,
  ApolloLink,
  from,
  fromPromise,
  InMemoryCache,
} from "@apollo/client";
import { createUploadLink } from "apollo-upload-client";
import { onError } from "@apollo/client/link/error";

import {
  errorEnumFromBeToMessageMap,
  errorMessagesForRequests,
  INTERNAL_ERROR_BE_MESSAGE,
  ITokens,
  refreshToken,
} from "api";
import { STORAGE_CONSTANTS } from "config/enums";
import {
  showSnackBar,
  store,
  NotificationDuration,
  NotificationMode,
  signOutAndClearStore,
} from "store";

const authLink = new ApolloLink((operation, forward) => {
  operation.setContext({
    headers: {
      authorization: `Bearer ${JSON.parse(
        localStorage.getItem(STORAGE_CONSTANTS.ACCESS_TOKEN) ?? "null"
      )}`,
      "Apollo-Require-Preflight": "true",
    },
  });

  return forward(operation);
});

const uploadLink = createUploadLink({
  uri: `${process.env.REACT_APP_BACKEND_URL}/graphql`,
});

const errorLink = onError(
  ({ graphQLErrors, networkError, forward, operation }) => {
    console.log(graphQLErrors, networkError, "ERRROR");
    const skipErrorToast = operation.getContext().skipErrorToast as boolean;

    if (graphQLErrors) {
      for (const err of graphQLErrors as any) {
        switch (err.extensions.code) {
          case "UNAUTHENTICATED": {
            const token = JSON.parse(
              localStorage.getItem(STORAGE_CONSTANTS.REFRESH_TOKEN) ?? "null"
            );
            return fromPromise(
              refreshToken(token).catch(() => {
                localStorage.clear();
                store.dispatch(
                  showSnackBar({
                    mode: NotificationMode.ERROR,
                    duration: NotificationDuration.ERRORS,
                    message: errorMessagesForRequests.expired,
                  })
                );
                setTimeout(() => {
                  store.dispatch(signOutAndClearStore());
                }, 3000);
                return;
              })
            )
              .filter((el): el is ITokens => Boolean(el))
              .flatMap(({ accessToken }) => {
                const oldHeaders = operation.getContext().headers;
                operation.setContext({
                  headers: {
                    ...oldHeaders,
                    authorization: `Bearer ${accessToken}`,
                  },
                });
                return forward(operation);
              });
          }
          case "INTERNAL_SERVER_ERROR": {
            if (!skipErrorToast) {
              const code = err?.extensions?.exception?.code ?? "";
              let message =
                errorEnumFromBeToMessageMap[
                  code as keyof typeof errorEnumFromBeToMessageMap
                ] ?? err?.message;
              if (!message || message === INTERNAL_ERROR_BE_MESSAGE) {
                message = errorMessagesForRequests.default;
              }
              store.dispatch(
                showSnackBar({
                  message: `${message} ${code ? `[code: ${code}]` : ""} `,
                  duration: NotificationDuration.ERRORS,
                  mode: NotificationMode.ERROR,
                })
              );
            }
            break;
          }
          default: {
            if (!skipErrorToast) {
              store.dispatch(
                showSnackBar({
                  message: `${err.extensions.code}: ${err.message} `,
                  duration: NotificationDuration.ERRORS,
                  mode: NotificationMode.ERROR,
                })
              );
            }
            throw err;
          }
        }
      }
    }

    if (networkError && !skipErrorToast) {
      store.dispatch(
        showSnackBar({
          message: errorMessagesForRequests.default,
          duration: NotificationDuration.ERRORS,
          mode: NotificationMode.ERROR,
        })
      );
    }
  }
);

// TTL constant: 6 hours for all cached data
const CACHE_TTL = 6 * 60 * 60 * 1000; // 6 hours in milliseconds

// Create a custom InMemoryCache with automatic TTL for all fields
class TTLInMemoryCache extends InMemoryCache {
  constructor(config?: any) {
    super(config);
  }

  // Override the read method to add TTL checking to ALL queries
  public read<T>(options: any): T | null {
    const result = super.read<T>(options);

    // If result exists and has a lastFetchTime, check TTL
    if (result && typeof result === "object" && "__lastFetchTime" in result) {
      const lastFetchTime = (result as any).__lastFetchTime;

      if (lastFetchTime && Date.now() - lastFetchTime > CACHE_TTL) {
        // Data is stale, return null to force refetch
        return null;
      }
    }

    return result;
  }

  // Override the write method to add timestamp to ALL cached data
  public write(options: any): any {
    // Add timestamp to the data being written
    if (options.data && typeof options.data === "object") {
      options.data = {
        ...options.data,
        __lastFetchTime: Date.now(),
      };
    }

    return super.write(options);
  }
}

export const client = new ApolloClient({
  link: from([authLink, errorLink, uploadLink as unknown as ApolloLink]),
  cache: new TTLInMemoryCache({
    typePolicies: {
      CommonListModel: {
        keyFields: false,
      },
    },
  }),
  connectToDevTools: process.env.NODE_ENV === "development",
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
      errorPolicy: "ignore",
      nextFetchPolicy: "cache-first",
    },
    query: {
      fetchPolicy: "network-only",
      errorPolicy: "all",
    },
  },
});
